import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Grid,
    Paper,
    CircularProgress,
    Alert,
    useTheme,
    Card,
    CardContent,
    CardActions,
    Avatar,
    alpha,
    TextField
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageHeader from '../ui/PageHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Icon mapping
const iconMap = {
    'calendar': CalendarMonthIcon,
    'sun': WbSunnyIcon,
    'register': HowToRegIcon,
    'default': EditNoteIcon
};

const Bookings = () => {
    const theme = useTheme();
    const { formId } = useParams();
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Password Protection State
    const [unlockedForms, setUnlockedForms] = useState([]);
    const [accessCodeInput, setAccessCodeInput] = useState('');
    const [unlockError, setUnlockError] = useState(false);

    useEffect(() => {
        fetch('/data/bookings.json')
            .then(response => {
                if (!response.ok) throw new Error('Failed to load bookings data');
                return response.json();
            })
            .then(data => {
                const activeForms = data.forms.filter(form => form.active);
                setForms(activeForms);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error loading bookings:', err);
                setError('Failed to load booking forms. Please try again later.');
                setLoading(false);
            });
    }, []);

    // Update selected form based on URL param
    useEffect(() => {
        if (forms.length > 0) {
            if (formId) {
                const foundForm = forms.find(f => f.id === formId);
                if (foundForm) {
                    setSelectedForm(foundForm);
                    // Reset gate state when switching forms
                    setAccessCodeInput('');
                    setUnlockError(false);
                } else {
                    setSelectedForm(null);
                }
            } else {
                setSelectedForm(null);
            }
        }
    }, [formId, forms]);

    const handleFormSelect = (form) => {
        navigate(`/bookings/${form.id}`);
    };

    const handleBack = () => {
        navigate('/bookings');
    };

    const handleUnlock = () => {
        if (selectedForm && selectedForm.accessCode === accessCodeInput.trim()) {
            setUnlockedForms([...unlockedForms, selectedForm.id]);
            setUnlockError(false);
        } else {
            setUnlockError(true);
        }
    };

    const getIconComponent = (iconName, color) => {
        const IconComponent = iconMap[iconName] || iconMap['default'];
        return <IconComponent fontSize="large" sx={{ color: color }} />;
    };

    // Check if current form is locked
    const isLocked = selectedForm && selectedForm.accessCode && !unlockedForms.includes(selectedForm.id);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>

            <PageHeader
                title={selectedForm ? 'Please complete your booking' : 'All Bookings'}
                subtitle={selectedForm ? selectedForm.title : 'Select a form below to enrol'}
            />

            {!selectedForm && !loading && !error && forms.length > 0 && (
                <Box
                    sx={{
                        mb: 4,
                        p: { xs: 2.5, sm: 3 },
                        borderRadius: 3,
                        backgroundColor: 'rgba(42, 56, 128, 0.6)',
                        border: '1px solid rgba(255,255,255,0.15)',
                    }}
                >
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                        <strong style={{ color: theme.palette.text.primary }}>New to SJ Swimming?</strong> Enrolment forms require an access code, which we send after an initial chat about your child's level and goals.{' '}
                        <strong style={{ color: theme.palette.text.primary }}>Call or email us first</strong> - we'll point you to the right program and get you set up from there.
                    </Typography>
                </Box>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && forms.length === 0 && (
                <Alert severity="info" sx={{ mb: 4 }}>
                    No booking forms are currently available. Please check back later.
                </Alert>
            )}

            {!loading && !error && forms.length > 0 && (
                <>
                    {!selectedForm ? (
                        /* Card Grid Menu */
                        <Box
                          component={motion.div}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: '-60px' }}
                          transition={{ duration: 0.5 }}
                        >
                        <Grid container spacing={4} justifyContent="center">
                            {forms.map((form) => (
                                <Grid item xs={12} sm={6} md={4} key={form.id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 4,
                                            backgroundColor: 'rgba(42, 56, 128, 0.6)',
                                            border: `1px solid rgba(255, 255, 255, 0.15)`,
                                            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: `0 12px 36px rgba(147, 197, 253, 0.2)`,
                                            },
                                        }}
                                    >
                                        <Box sx={{
                                            p: 3,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}>
                                            <Avatar
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    background: 'rgba(147, 197, 253, 0.15)',
                                                    color: '#93c5fd',
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                {getIconComponent(form.icon, '#93c5fd')}
                                            </Avatar>
                                        </Box>
                                        <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                                            <Typography gutterBottom variant="h5" component="div" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                                                {form.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                {form.description}
                                            </Typography>
                                            {form.accessCode && (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.warning.main, fontWeight: 'bold' }}>
                                                    <LockIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                                                    Access Code Required
                                                </Typography>
                                            )}
                                        </CardContent>
                                        <CardActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                fullWidth
                                                onClick={() => handleFormSelect(form)}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '1.1rem' }}
                                            >
                                                Book Now
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        </Box>
                    ) : (
                        /* Selected Form View */
                        <Box>
                            {/* Back Button */}
                            <Box sx={{ mb: 3 }}>
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={handleBack}
                                    variant="text"
                                    size="large"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Back to Menu
                                </Button>
                            </Box>

                            {/* Unlocked Form or Gate */}
                            <Paper
                                elevation={3}
                                sx={{
                                    p: { xs: 0, md: 3 },
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    backgroundColor: '#fffffff7',
                                    minHeight: '600px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: isLocked ? 'center' : 'flex-start'
                                }}
                            >
                                {isLocked ? (
                                    /* 🔒 The Gate UI */
                                    <Box sx={{ maxWidth: 450, mx: 'auto', textAlign: 'center', p: 5 }}>
                                        <Avatar sx={{
                                            bgcolor: alpha(theme.palette.error.main, 0.1),
                                            color: theme.palette.error.main,
                                            width: 80,
                                            height: 80,
                                            mx: 'auto',
                                            mb: 3
                                        }}>
                                            <LockIcon sx={{ fontSize: 40 }} />
                                        </Avatar>
                                        <Typography variant="h4" gutterBottom sx={{ color: '#1a1a1a', fontWeight: 'bold' }}>
                                            Restricted Access
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 4, color: '#555', fontSize: '1.1rem' }}>
                                            This booking form is private. We should have given you the password for this form.
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Enter Access Code"
                                            type="password"
                                            variant="outlined"
                                            value={accessCodeInput}
                                            onChange={(e) => {
                                                setAccessCodeInput(e.target.value);
                                                setUnlockError(false);
                                            }}
                                            error={unlockError}
                                            helperText={unlockError ? "Incorrect access code. Please try again." : ""}
                                            sx={{
                                                mb: 4,
                                                // Hardcoded Label Color
                                                '& .MuiInputLabel-root': {
                                                    color: '#333333',
                                                    '&.Mui-focused': {
                                                        color: '#333333',
                                                    }
                                                },
                                                // Hardcoded Input Colors
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: '#fafafa',
                                                    fontSize: '1.2rem',
                                                    color: '#333333', // Input Text
                                                    '& fieldset': {
                                                        borderWidth: '2px',
                                                        borderColor: '#333333', // Default Outline
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#000000', // Hover Outline
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#333333', // Focused Outline
                                                    }
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleUnlock();
                                            }}
                                        />

                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            onClick={handleUnlock}
                                            startIcon={<LockOpenIcon />}
                                            sx={{
                                                py: 1.5,
                                                fontSize: '1.1rem',
                                                borderRadius: 2,
                                                boxShadow: theme.shadows[4]
                                            }}
                                        >
                                            Unlock Form
                                        </Button>
                                    </Box>
                                ) : (
                                    /* 🔓 The Unlocked Form */
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                        <iframe
                                            src={selectedForm.embedUrl}
                                            width={selectedForm.width || "100%"}
                                            height={selectedForm.height || "800"}
                                            title={selectedForm.title}
                                            frameBorder={selectedForm.frameBorder || "0"}
                                            marginHeight={selectedForm.marginHeight || "0"}
                                            marginWidth={selectedForm.marginWidth || "0"}
                                            style={{ maxWidth: '100%', border: 'none' }}
                                        >
                                            Loading…
                                        </iframe>
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
};

export default Bookings;
